import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
    Clock, ChevronRight, ChevronLeft, ArrowRight, TrendingUp,
    Bike, Car, Star, Play, Search, Mail, Target, Ticket,
    BookOpen, MapPin, UtensilsCrossed, Lightbulb, Plane, Newspaper, Folder, Map, Camera, Coffee, Heart, Globe,
    Home, Users, Calendar, Tag, Music, Film, Image as ImageIcon, Sun, Moon, Cloud, Umbrella, Compass, Anchor, Award, Gift, ShoppingBag, ThumbsUp, Zap, Info, HelpCircle
} from 'lucide-react'
import { formatPrice } from '../../lib/utils'
import type { SectionConfig } from '../../lib/sections'

// Icon mapping for rendering category icons from text names
const iconMap: Record<string, any> = {
    'bookopen': BookOpen, 'book-open': BookOpen,
    'mappin': MapPin, 'map-pin': MapPin,
    'utensilscrossed': UtensilsCrossed, 'utensils-crossed': UtensilsCrossed,
    'lightbulb': Lightbulb, 'plane': Plane, 'newspaper': Newspaper,
    'folder': Folder, 'map': Map, 'camera': Camera, 'coffee': Coffee,
    'star': Star, 'heart': Heart, 'globe': Globe, 'target': Target,
    'bike': Bike, 'car': Car, 'ticket': Ticket, 'calendar': Calendar,
    'users': Users, 'home': Home, 'tag': Tag, 'music': Music, 'film': Film,
    'compass': Compass, 'anchor': Anchor, 'award': Award, 'gift': Gift,
}
const renderCatIcon = (iconName: string | undefined, className = 'w-4 h-4') => {
    if (!iconName) return <Folder className={className} />
    const IconComponent = iconMap[iconName.toLowerCase().trim()] || Folder
    return <IconComponent className={className} />
}

interface RenderProps {
    config: SectionConfig
    data: any
    language: 'en' | 'vi'
    currency: 'USD' | 'VND'
    exchangeRate: number
}

// ==================== HERO SECTIONS ====================

// Hero với Slideshow - Full-width image với title overlay
export function HeroSection({ config, data, language }: RenderProps) {
    const articles = data?.articles || []
    const [currentSlide, setCurrentSlide] = useState(0)
    const count = Math.min(config.config?.count || 5, articles.length)

    // Auto slideshow
    useEffect(() => {
        if ((config.variant === 'slideshow' || config.variant === 'fullwidth') && count > 1) {
            const timer = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % count)
            }, 5000)
            return () => clearInterval(timer)
        }
    }, [config.variant, count])

    if (articles.length === 0) return null

    const getTitle = (a: any) => language === 'en' ? a.title_en : (a.title_vi || a.title_en)
    const getExcerpt = (a: any) => language === 'en' ? a.excerpt_en : (a.excerpt_vi || a.excerpt_en)

    // Variant: Full-width Slideshow (ảnh full, title overlay)
    if (config.variant === 'slideshow' || config.variant === 'fullwidth') {
        const featured = articles[currentSlide]
        return (
            <section className="relative w-full overflow-hidden">
                {/* Full-width Background - Fully Clickable */}
                <Link
                    to={`/blog/${featured.category}/${featured.slug}`}
                    className="block relative h-[300px] md:h-[400px] lg:h-[500px] group cursor-pointer"
                >
                    <img
                        src={featured.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=1200'}
                        alt={getTitle(featured)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/70 transition-colors" />

                    {/* Content Overlay */}
                    <div className="absolute inset-0 flex items-end">
                        <div className="container mx-auto px-4 pb-8 md:pb-12">
                            <div className="max-w-2xl">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500 rounded-full text-sm font-medium text-white mb-4">
                                    <Star className="w-4 h-4" /> {language === 'en' ? 'Featured' : 'Nổi bật'}
                                </span>
                                <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 leading-tight text-white group-hover:text-orange-200 transition-colors">
                                    {getTitle(featured)}
                                </h1>
                                <p className="text-gray-200 mb-4 md:mb-6 line-clamp-2 text-sm md:text-base lg:text-lg">
                                    {getExcerpt(featured)}
                                </p>
                                <span className="inline-flex items-center gap-2 text-white/80 text-sm group-hover:text-orange-300 transition-colors">
                                    <ArrowRight className="w-4 h-4" /> {language === 'en' ? 'Click to read' : 'Nhấn để đọc'}
                                </span>
                            </div>
                        </div>
                    </div>
                </Link>

                {/* Slide Indicators */}
                {count > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                        {articles.slice(0, count).map((_: any, idx: number) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentSlide(idx)}
                                className={`h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-2 hover:bg-white/70'
                                    }`}
                            />
                        ))}
                    </div>
                )}

                {/* Nav Arrows */}
                {count > 1 && (
                    <>
                        <button
                            onClick={() => setCurrentSlide((prev) => (prev - 1 + count) % count)}
                            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center transition text-white"
                        >
                            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                        </button>
                        <button
                            onClick={() => setCurrentSlide((prev) => (prev + 1) % count)}
                            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center transition text-white"
                        >
                            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                        </button>
                    </>
                )}
            </section>
        )
    }

    // Variant: Featured + Thumbnails (giữ lại)
    if (config.variant === 'featured-thumbnails') {
        const featured = articles[0]
        const thumbs = articles.slice(1, 4)
        return (
            <section className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-6 md:py-10 overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Featured Large */}
                        <div className="lg:col-span-2">
                            <Link to={`/blog/${featured.category}/${featured.slug}`} className="block group">
                                <div className="relative h-[280px] md:h-[400px] rounded-2xl overflow-hidden">
                                    <img
                                        src={featured.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=800'}
                                        alt={getTitle(featured)}
                                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <span className="inline-block px-3 py-1 bg-orange-500 rounded-full text-sm font-medium mb-3">
                                            {language === 'en' ? 'Featured' : 'Nổi bật'}
                                        </span>
                                        <h2 className="text-2xl md:text-3xl font-bold mb-2 group-hover:text-orange-300 transition">
                                            {getTitle(featured)}
                                        </h2>
                                        <p className="text-gray-200 line-clamp-2 text-sm md:text-base">{getExcerpt(featured)}</p>
                                    </div>
                                </div>
                            </Link>
                        </div>

                        {/* Thumbnails */}
                        <div className="space-y-4">
                            {thumbs.map((article: any) => (
                                <Link key={article.id} to={`/blog/${article.category}/${article.slug}`} className="flex gap-4 group">
                                    <img
                                        src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=300'}
                                        alt={getTitle(article)}
                                        className="w-28 h-20 md:w-32 md:h-24 object-cover rounded-lg flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm md:text-base line-clamp-2 group-hover:text-orange-300 transition">
                                            {getTitle(article)}
                                        </h3>
                                        <p className="text-primary-200 text-xs mt-1 line-clamp-1">{getExcerpt(article)}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // Default: Simple Featured
    const featured = articles[0]
    return (
        <section className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-8 md:py-12 overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div>
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500 rounded-full text-sm font-medium mb-4">
                            <Star className="w-4 h-4" /> {language === 'en' ? 'Featured' : 'Nổi bật'}
                        </span>
                        <h1 className="text-2xl md:text-4xl font-bold mb-4 leading-tight">
                            {getTitle(featured)}
                        </h1>
                        <p className="text-primary-100 mb-6 line-clamp-3 text-base md:text-lg">
                            {getExcerpt(featured)}
                        </p>
                        <Link
                            to={`/blog/${featured.category}/${featured.slug}`}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-orange-500 hover:text-white transition"
                        >
                            {language === 'en' ? 'Read More' : 'Đọc thêm'} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div>
                        <img
                            src={featured.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=800'}
                            alt={getTitle(featured)}
                            className="w-full h-[250px] md:h-[350px] object-cover rounded-2xl shadow-2xl"
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

// ==================== CATEGORY SECTIONS ====================

export function CategorySection({ config, data, language }: RenderProps) {
    const articles = data?.articles || []
    const category = data?.category

    if (articles.length === 0) return null

    const getTitle = (a: any) => language === 'en' ? a.title_en : (a.title_vi || a.title_en)
    const getExcerpt = (a: any) => language === 'en' ? a.excerpt_en : (a.excerpt_vi || a.excerpt_en)
    const getCatName = (c: any) => c ? (language === 'en' ? c.name_en : c.name_vi) : ''

    // Use variant from Page Builder first, then fallback to config.layout
    const layout = config.variant || config.config?.layout || 'grid-3'

    // Header component
    const SectionHeader = () => (
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
                {renderCatIcon(category?.icon, 'w-5 h-5 md:w-6 md:h-6')}
                <span className="truncate">{getCatName(category)}</span>
            </h2>
            <Link to={`/blog/${category?.slug}`} className="text-primary-600 text-sm font-medium flex items-center gap-1 hover:underline whitespace-nowrap">
                {language === 'en' ? 'View all' : 'Xem tất cả'} <ChevronRight className="w-4 h-4" />
            </Link>
        </div>
    )

    // Layout 1: Featured Left + Sidebar Right - Simple mobile-first
    if (layout === 'featured-sidebar') {
        const featured = articles[0]
        const sidebar = articles.slice(1, 4)

        return (
            <section className="bg-white rounded-xl p-4 border shadow-sm">
                <SectionHeader />
                {/* Simple stacked layout on mobile, side-by-side on lg */}
                <div className="space-y-4 lg:space-y-0 lg:flex lg:gap-6">
                    {/* Featured - Full width on mobile */}
                    <div className="lg:w-3/5">
                        <Link to={`/blog/${featured.category}/${featured.slug}`} className="block group">
                            <div className="relative h-[180px] md:h-[280px] rounded-xl overflow-hidden">
                                <img
                                    src={featured.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=800'}
                                    alt={getTitle(featured)}
                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-600 rounded text-xs font-medium mb-2">
                                        {renderCatIcon(category?.icon, 'w-3 h-3')} {getCatName(category)}
                                    </span>
                                    <h3 className="text-base md:text-xl font-bold line-clamp-2 group-hover:text-orange-300 transition">
                                        {getTitle(featured)}
                                    </h3>
                                </div>
                            </div>
                        </Link>
                    </div>

                    {/* Sidebar articles - Simple list */}
                    <div className="lg:w-2/5 space-y-3">
                        {sidebar.map((article: any) => (
                            <Link
                                key={article.id}
                                to={`/blog/${article.category}/${article.slug}`}
                                className="flex gap-3 group"
                            >
                                <img
                                    src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=300'}
                                    alt={getTitle(article)}
                                    className="w-20 md:w-24 h-16 md:h-20 object-cover rounded-lg flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                    <h4 className="font-semibold text-sm line-clamp-2 group-hover:text-primary-600 transition">
                                        {getTitle(article)}
                                    </h4>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    // Layout 2: Grid
    if (layout === 'grid-3' || layout === 'grid-2') {
        const columns = layout === 'grid-2' ? 2 : 3
        const gridClass = columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 md:grid-cols-3'
        return (
            <section className="bg-white rounded-2xl p-4 md:p-6 border shadow-sm overflow-hidden">
                <SectionHeader />
                <div className={`grid grid-cols-1 ${gridClass} gap-4 md:gap-5`}>
                    {articles.map((article: any) => (
                        <Link key={article.id} to={`/blog/${article.category}/${article.slug}`} className="group">
                            <div className="h-[180px] md:h-[200px] rounded-xl overflow-hidden mb-2 md:mb-3">
                                <img
                                    src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=500'}
                                    alt={getTitle(article)}
                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                />
                            </div>
                            <h3 className="font-bold text-sm md:text-base line-clamp-2 group-hover:text-primary-600 transition mb-1">
                                {getTitle(article)}
                            </h3>
                            <p className="text-gray-500 text-xs md:text-sm line-clamp-2">{getExcerpt(article)}</p>
                        </Link>
                    ))}
                </div>
            </section>
        )
    }

    // Layout 3: Magazine (1 lớn + 2 nhỏ dọc)
    if (layout === 'magazine') {
        const featured = articles[0]
        const rest = articles.slice(1, 3)
        return (
            <section className="bg-white rounded-2xl p-4 md:p-6 border shadow-sm overflow-hidden">
                <SectionHeader />
                <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                    {/* Large */}
                    <Link to={`/blog/${featured.category}/${featured.slug}`} className="group">
                        <div className="h-[200px] md:h-[280px] rounded-xl overflow-hidden mb-2 md:mb-3">
                            <img
                                src={featured.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=600'}
                                alt={getTitle(featured)}
                                className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                            />
                        </div>
                        <h3 className="font-bold text-base md:text-lg line-clamp-2 group-hover:text-primary-600 transition mb-1">
                            {getTitle(featured)}
                        </h3>
                        <p className="text-gray-500 text-xs md:text-sm line-clamp-2">{getExcerpt(featured)}</p>
                    </Link>

                    {/* Smaller stacked */}
                    <div className="space-y-4">
                        {rest.map((article: any) => (
                            <Link key={article.id} to={`/blog/${article.category}/${article.slug}`} className="flex gap-3 md:gap-4 group">
                                <img
                                    src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=300'}
                                    alt={getTitle(article)}
                                    className="w-28 md:w-36 h-[80px] md:h-[100px] object-cover rounded-lg flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-sm md:text-base line-clamp-2 group-hover:text-primary-600 transition">
                                        {getTitle(article)}
                                    </h4>
                                    <p className="text-gray-500 text-xs mt-1 line-clamp-2">{getExcerpt(article)}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    // Layout 4: List
    if (layout === 'list') {
        return (
            <section className="bg-white rounded-2xl p-4 md:p-6 border shadow-sm overflow-hidden">
                <SectionHeader />
                <div className="divide-y">
                    {articles.map((article: any) => (
                        <Link key={article.id} to={`/blog/${article.category}/${article.slug}`} className="flex gap-3 md:gap-4 py-3 md:py-4 first:pt-0 last:pb-0 group">
                            <img
                                src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=300'}
                                alt={getTitle(article)}
                                className="w-24 md:w-32 h-[70px] md:h-[90px] object-cover rounded-lg flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-sm md:text-base line-clamp-2 group-hover:text-primary-600 transition">
                                    {getTitle(article)}
                                </h4>
                                <p className="text-gray-500 text-xs mt-1 line-clamp-1 md:line-clamp-2">{getExcerpt(article)}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        )
    }

    // Layout 5: Carousel ngang
    if (layout === 'carousel') {
        return (
            <section className="bg-white rounded-2xl p-4 md:p-6 border shadow-sm overflow-hidden">
                <SectionHeader />
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                    {articles.map((article: any) => (
                        <Link
                            key={article.id}
                            to={`/blog/${article.category}/${article.slug}`}
                            className="flex-shrink-0 w-[200px] md:w-[260px] snap-start group"
                        >
                            <div className="h-[170px] md:h-[200px] rounded-xl overflow-hidden mb-2 md:mb-3">
                                <img
                                    src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=400'}
                                    alt={getTitle(article)}
                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                />
                            </div>
                            <h4 className="font-bold text-sm md:text-base line-clamp-2 group-hover:text-primary-600 transition">
                                {getTitle(article)}
                            </h4>
                        </Link>
                    ))}
                </div>
            </section>
        )
    }

    // Fallback: Grid 3
    return (
        <section className="bg-white rounded-2xl p-4 md:p-6 border shadow-sm overflow-hidden">
            <SectionHeader />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                {articles.map((article: any) => (
                    <Link key={article.id} to={`/blog/${article.category}/${article.slug}`} className="group">
                        <div className="h-[180px] md:h-[200px] rounded-xl overflow-hidden mb-2 md:mb-3">
                            <img
                                src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=500'}
                                alt={getTitle(article)}
                                className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                            />
                        </div>
                        <h3 className="font-bold text-sm md:text-base line-clamp-2 group-hover:text-primary-600 transition">
                            {getTitle(article)}
                        </h3>
                    </Link>
                ))}
            </div>
        </section>
    )
}

// ==================== TOURS SECTION ====================

export function ToursSection({ config, data, language, currency, exchangeRate }: RenderProps) {
    const tours = data?.tours || []
    if (tours.length === 0) return null

    const getTourTitle = (t: any) => {
        // Check multiple possible field locations for tour title
        if (language === 'en') {
            return t.title_en || t.translations?.en?.title || t.title || t.name || 'Tour'
        }
        return t.title_vi || t.translations?.vi?.title || t.title_en || t.translations?.en?.title || t.title || t.name || 'Tour'
    }

    return (
        <section className="relative rounded-2xl overflow-hidden">
            {/* Premium dark gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

            {/* Animated accent gradient */}
            <div className="absolute inset-0 opacity-40">
                <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-500/30 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
            </div>

            {/* Glass border effect */}
            <div className="absolute inset-0 rounded-2xl border border-white/10" />

            <div className="relative p-5 md:p-7">
                {/* Header with accent line */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                            <Ticket className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-white">
                                {language === 'en' ? 'Popular Tours' : 'Tour Nổi Bật'}
                            </h2>
                            <p className="text-xs text-slate-400">{language === 'en' ? 'Best experiences on the island' : 'Trải nghiệm tốt nhất'}</p>
                        </div>
                    </div>
                    <Link to="/tours" className="group flex items-center gap-1.5 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/80 hover:text-white text-sm font-medium transition-all">
                        {language === 'en' ? 'View all' : 'Xem tất cả'}
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>

                {/* Tours Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {tours.slice(0, config.config?.count || 8).map((tour: any) => (
                        <Link
                            key={tour.id}
                            to={`/tours/${tour.id}`}
                            className="group relative bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300"
                        >
                            {/* Image with overlay */}
                            <div className="aspect-[4/3] overflow-hidden">
                                <img
                                    src={tour.featuredImage || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400'}
                                    alt={getTourTitle(tour)}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />

                                {/* Price badge */}
                                <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-lg shadow-lg">
                                    {formatPrice(tour.price, currency, exchangeRate)}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="absolute bottom-0 left-0 right-0 p-2.5 md:p-3">
                                <h3 className="font-semibold text-xs md:text-sm text-white line-clamp-1 leading-tight group-hover:text-emerald-300 transition-colors">
                                    {getTourTitle(tour)}
                                </h3>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    )
}

// ==================== VEHICLES SECTION ====================

export function VehiclesSection({ config, data, language, currency, exchangeRate }: RenderProps) {
    const vehicles = data?.vehicles || []
    if (vehicles.length === 0) return null

    return (
        <section className="bg-white rounded-2xl p-4 md:p-6 border shadow-sm overflow-hidden">
            <div className="flex items-center justify-between mb-4 md:mb-5 flex-wrap gap-2">
                <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
                    <Bike className="w-5 h-5" /> {language === 'en' ? 'Rent a Vehicle' : 'Thuê Xe'}
                </h2>
                <Link to="/vehicles" className="text-primary-600 text-sm font-medium flex items-center gap-1 hover:underline">
                    {language === 'en' ? 'All vehicles' : 'Tất cả'} <ChevronRight className="w-4 h-4" />
                </Link>
            </div>
            {/* Mobile: horizontal scroll, Desktop: grid */}
            <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible">
                {vehicles.slice(0, config.config?.count || 6).map((v: any) => (
                    <Link
                        key={v.id}
                        to={`/vehicles/${v.slug || v.id}`}
                        className="flex-shrink-0 w-[140px] md:w-auto group border rounded-xl overflow-hidden hover:shadow-lg transition bg-white snap-start"
                    >
                        <div className="h-[100px] md:h-[130px] bg-gray-100 flex items-center justify-center overflow-hidden">
                            {v.images?.[0] ? (
                                <img src={v.images[0]} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover group-hover:scale-110 transition" />
                            ) : (
                                v.type === 'motorcycle' ? <Bike className="w-8 h-8 text-gray-300" /> : <Car className="w-8 h-8 text-gray-300" />
                            )}
                        </div>
                        <div className="p-2 md:p-3">
                            <h4 className="font-bold text-xs md:text-sm line-clamp-1 mb-1">{v.brand} {v.model}</h4>
                            <div className="flex items-center justify-between">
                                <span className="text-primary-600 font-bold text-xs md:text-sm">
                                    {formatPrice(v.dailyRate, currency, exchangeRate)}
                                    <span className="text-gray-400 font-normal text-[10px] md:text-xs">/{language === 'en' ? 'day' : 'ngày'}</span>
                                </span>
                                {v.rating > 0 && (
                                    <div className="flex items-center text-yellow-500 text-xs">
                                        <Star className="w-3 h-3 fill-current" /> {v.rating.toFixed(1)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    )
}

// ==================== VIDEOS SECTION ====================

export function VideosSection({ config, language }: RenderProps) {
    const [videos, setVideos] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const res = await fetch(`/api/shorts?limit=${config.config?.count || 6}&lang=${language}`)
                const data = await res.json()
                if (data.success && data.data) {
                    setVideos(data.data)
                }
            } catch (error) {
                console.error('Failed to fetch videos:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchVideos()
    }, [language, config.config?.count])

    const getYoutubeThumbnail = (url: string) => {
        const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
        if (match) return `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg`
        return ''
    }

    return (
        <section className="relative rounded-2xl overflow-hidden">
            {/* Premium dark gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-black via-slate-900 to-black" />

            {/* Animated accent gradients */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-20 -left-20 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-fuchsia-600/15 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-32 bg-gradient-to-r from-transparent via-violet-500/5 to-transparent" />
            </div>

            {/* Glass border */}
            <div className="absolute inset-0 rounded-2xl border border-white/10" />

            <div className="relative p-5 md:p-7">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                                <Play className="w-5 h-5 text-white ml-0.5" />
                            </div>
                            {/* Live indicator dot */}
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-black animate-pulse" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-white">
                                {language === 'en' ? 'Short Videos' : 'Video Ngắn'}
                            </h2>
                            <p className="text-xs text-slate-400">{language === 'en' ? 'Trending content' : 'Nội dung thịnh hành'}</p>
                        </div>
                    </div>
                    <Link to="/shorts" className="group flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 hover:from-violet-500/30 hover:to-fuchsia-500/30 border border-violet-500/30 rounded-full text-white/90 hover:text-white text-sm font-medium transition-all">
                        {language === 'en' ? 'Watch all' : 'Xem tất cả'}
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>

                {/* Videos Grid - horizontal scroll on all sizes */}
                <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="flex-shrink-0 w-[120px] md:w-[140px] aspect-[9/16] bg-white/5 backdrop-blur rounded-2xl animate-pulse snap-start"
                            />
                        ))
                    ) : videos.length > 0 ? (
                        videos.map((video, idx) => (
                            <Link
                                key={video.id}
                                to={`/shorts/${video.id}`}
                                className="flex-shrink-0 w-[120px] md:w-[140px] aspect-[9/16] rounded-2xl overflow-hidden relative group snap-start"
                                style={{ animationDelay: `${idx * 50}ms` }}
                            >
                                {/* Ring border effect */}
                                <div className="absolute inset-0 rounded-2xl border-2 border-white/10 group-hover:border-violet-500/50 transition-colors z-10" />

                                {/* Video thumbnail */}
                                <img
                                    src={video.thumbnail_url || getYoutubeThumbnail(video.video_url)}
                                    alt={video.title || 'Video'}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    onError={(e) => {
                                        e.currentTarget.src = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=300&h=500&fit=crop'
                                    }}
                                />

                                {/* Gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80" />

                                {/* Floating play button */}
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
                                    <div className="w-12 h-12 bg-white/95 rounded-full flex items-center justify-center shadow-lg scale-90 group-hover:scale-100 transition-transform">
                                        <Play className="w-6 h-6 text-violet-600 ml-1" fill="currentColor" />
                                    </div>
                                </div>

                                {/* Content overlay */}
                                <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                                    <p className="text-xs font-semibold text-white line-clamp-2 mb-1.5 drop-shadow-lg">
                                        {video.title || 'Video'}
                                    </p>
                                    <div className="flex items-center gap-1.5 text-[10px] text-white/70">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                        {(video.views || 0).toLocaleString()}
                                    </div>
                                </div>

                                {/* Top ranking badge for first 3 */}
                                {idx < 3 && (
                                    <div className="absolute top-2 left-2 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-black shadow-lg z-10">
                                        {idx + 1}
                                    </div>
                                )}
                            </Link>
                        ))
                    ) : (
                        <div className="w-full text-center py-8 text-white/40 text-sm">
                            {language === 'en' ? 'No videos yet' : 'Chưa có video'}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

// ==================== SIDEBAR SECTIONS ====================

export function SearchSection({ language }: { language: string }) {
    return (
        <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder={language === 'en' ? 'Search articles...' : 'Tìm bài viết...'}
                    className="w-full pl-10 pr-4 py-2.5 md:py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
            </div>
        </div>
    )
}

export function CategoriesSection({ data, language, config }: { data: any, language: string, config: any }) {
    const categories = data?.categories || []
    const articlesByCategory = data?.articlesByCategory || {}
    const getCatName = (c: any) => language === 'en' ? c.name_en : c.name_vi

    // Helper to render category icon (supports Lucide icon names)
    const renderCatIcon = (iconName: string) => {
        if (!iconName) return <Folder className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />

        // Comprehensive Lucide icon mapping (case-insensitive)
        const iconMap: Record<string, any> = {
            // Common icons
            'bookopen': BookOpen,
            'book-open': BookOpen,
            'mappin': MapPin,
            'map-pin': MapPin,
            'utensilscrossed': UtensilsCrossed,
            'utensils-crossed': UtensilsCrossed,
            'lightbulb': Lightbulb,
            'plane': Plane,
            'newspaper': Newspaper,
            'folder': Folder,
            'map': Map,
            'camera': Camera,
            'coffee': Coffee,
            'star': Star,
            'heart': Heart,
            'globe': Globe,
            // Additional icons
            'car': Car,
            'home': Home,
            'users': Users,
            'calendar': Calendar,
            'clock': Clock,
            'tag': Tag,
            'music': Music,
            'film': Film,
            'image': ImageIcon,
            'sun': Sun,
            'moon': Moon,
            'cloud': Cloud,
            'umbrella': Umbrella,
            'compass': Compass,
            'anchor': Anchor,
            'award': Award,
            'gift': Gift,
            'shopping-bag': ShoppingBag,
            'shoppingbag': ShoppingBag,
            'thumbs-up': ThumbsUp,
            'thumbsup': ThumbsUp,
            'zap': Zap,
            'info': Info,
            'help-circle': HelpCircle,
            'helpcircle': HelpCircle,
        }

        // Try exact match (lowercase)
        const normalizedName = iconName.toLowerCase().trim()
        const IconComponent = iconMap[normalizedName]

        if (IconComponent) {
            return <IconComponent className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
        }

        // Fallback to folder icon
        return <Folder className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
    }

    return (
        <div className="bg-white rounded-xl p-4 border shadow-sm">
            <h3 className="font-bold text-sm md:text-base mb-3 md:mb-4 flex items-center gap-2">
                <Folder className="w-4 md:w-5 h-4 md:h-5 text-primary-600" />
                {language === 'en' ? 'Categories' : 'Danh Mục'}
            </h3>
            <div className="space-y-1 md:space-y-2">
                {categories.map((cat: any) => (
                    <Link key={cat.id} to={`/blog/${cat.slug}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition">
                        <span className="flex items-center gap-2">
                            {renderCatIcon(cat.icon)}
                            <span className="text-xs md:text-sm">{getCatName(cat)}</span>
                        </span>
                        {config?.showCount && (
                            <span className="text-[10px] md:text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                {articlesByCategory[cat.slug]?.length || 0}
                            </span>
                        )}
                    </Link>
                ))}
            </div>
        </div>
    )
}

export function PopularSection({ data, language }: { data: any, language: string }) {
    const articles = data?.articles || []
    const getTitle = (a: any) => language === 'en' ? a.title_en : (a.title_vi || a.title_en)

    return (
        <div className="bg-white rounded-xl p-4 border shadow-sm">
            <h3 className="font-bold text-sm md:text-base mb-3 md:mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 md:w-5 h-4 md:h-5 text-primary-600" />
                {language === 'en' ? 'Popular' : 'Phổ biến'}
            </h3>
            <div className="space-y-2 md:space-y-3">
                {articles.slice(0, 5).map((article: any, idx: number) => (
                    <Link key={article.id} to={`/blog/${article.category}/${article.slug}`} className="flex gap-2 md:gap-3 group">
                        <span className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0 flex items-center justify-center bg-primary-100 text-primary-600 font-bold rounded text-xs md:text-sm">
                            {idx + 1}
                        </span>
                        <h4 className="text-xs md:text-sm font-medium line-clamp-2 group-hover:text-primary-600 transition">
                            {getTitle(article)}
                        </h4>
                    </Link>
                ))}
            </div>
        </div>
    )
}

export function CTASection({ config, language }: { config: any, language: string }) {
    const gradients: Record<string, string> = {
        'orange-red': 'from-orange-500 to-red-500',
        'blue-purple': 'from-blue-500 to-purple-500',
        'green': 'from-green-500 to-emerald-500',
    }
    const gradient = gradients[config?.gradient] || gradients['orange-red']

    return (
        <div className={`bg-gradient-to-br ${gradient} rounded-xl p-4 md:p-5 text-white`}>
            <h3 className="font-bold text-base md:text-lg mb-2 flex items-center gap-2">
                <Target className="w-5 h-5" /> {config?.title || (language === 'en' ? 'Book a Tour!' : 'Đặt Tour Ngay!')}
            </h3>
            <p className="text-xs md:text-sm opacity-90 mb-3 md:mb-4">
                {language === 'en' ? 'Explore beautiful Phú Quốc with our guided tours.' : 'Khám phá Phú Quốc với các tour có hướng dẫn.'}
            </p>
            <Link to={config?.link || '/tours'} className="block w-full py-2 md:py-2.5 bg-white text-orange-600 font-semibold rounded-lg text-center hover:bg-gray-100 transition text-xs md:text-sm">
                {language === 'en' ? 'Explore Tours' : 'Xem Tour'}
            </Link>
        </div>
    )
}

// Newsletter với Sticky - mặc định sticky trên desktop
export function NewsletterSection({ language, sticky = true }: { language: string, sticky?: boolean }) {
    return (
        <div className={`bg-white rounded-xl p-4 border shadow-sm ${sticky ? 'lg:sticky lg:top-4' : ''}`}>
            <h3 className="font-bold text-sm md:text-base mb-2 flex items-center gap-2">
                <Mail className="w-4 md:w-5 h-4 md:h-5 text-primary-600" />
                {language === 'en' ? 'Newsletter' : 'Đăng ký'}
            </h3>
            <p className="text-xs md:text-sm text-gray-600 mb-3">
                {language === 'en' ? 'Get travel tips & deals' : 'Nhận mẹo du lịch & ưu đãi'}
            </p>
            <form className="space-y-2">
                <input type="email" placeholder="Email" className="w-full px-3 py-2 md:py-2.5 border rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-primary-500" />
                <button className="w-full py-2 md:py-2.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition text-xs md:text-sm">
                    {language === 'en' ? 'Subscribe' : 'Đăng ký'}
                </button>
            </form>
        </div>
    )
}

export function AdsSection({ config }: { config: any }) {
    if (config?.variant === 'custom-html' && config?.html) {
        return <div className="rounded-xl overflow-hidden" dangerouslySetInnerHTML={{ __html: config.html }} />
    }

    if (config?.imageUrl) {
        return (
            <a href={config.link || '#'} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden">
                <img src={config.imageUrl} alt="Advertisement" className="w-full" />
            </a>
        )
    }

    return null
}

// ==================== VIDEO SECTION ====================
export function VideoSection({ language, config }: { language: string, config?: any }) {
    const [videos, setVideos] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const res = await fetch(`/api/shorts?limit=${config?.count || 5}&lang=${language}`)
                const data = await res.json()
                if (data.success && data.data) {
                    setVideos(data.data)
                }
            } catch (error) {
                console.error('Failed to fetch videos:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchVideos()
    }, [language, config?.count])

    const getYoutubeThumbnail = (url: string) => {
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
        if (match) return `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg`
        return url
    }

    const formatViews = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
        return num?.toString() || '0'
    }

    if (loading) {
        return (
            <div className="bg-white rounded-xl p-4 border shadow-sm">
                <div className="animate-pulse space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                    <div className="flex gap-3">
                        {[1, 2, 3].map(i => <div key={i} className="w-24 h-40 bg-gray-200 rounded"></div>)}
                    </div>
                </div>
            </div>
        )
    }

    if (videos.length === 0) return null

    return (
        <div className="bg-white rounded-xl p-4 border shadow-sm">
            <h3 className="font-bold text-sm md:text-base mb-3 md:mb-4 flex items-center gap-2">
                <Play className="w-4 md:w-5 h-4 md:h-5 text-primary-600" />
                {language === 'en' ? 'Videos' : 'Video'}
            </h3>
            <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {videos.map((video) => (
                    <Link
                        key={video.id}
                        to="/shorts"
                        className="flex-shrink-0 w-24 md:w-28 group"
                    >
                        <div className="relative aspect-[9/16] rounded-lg overflow-hidden bg-gray-100">
                            <img
                                src={video.thumbnail_url || getYoutubeThumbnail(video.video_url)}
                                alt={video.title || 'Video'}
                                className="w-full h-full object-cover group-hover:scale-105 transition"
                                onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/100x178?text=Video' }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-2">
                                <p className="text-white text-[10px] md:text-xs line-clamp-2">{video.title || 'Video'}</p>
                                <p className="text-gray-300 text-[9px] md:text-[10px] mt-0.5">{formatViews(video.views)} views</p>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                                <div className="w-8 h-8 md:w-10 md:h-10 bg-white/90 rounded-full flex items-center justify-center">
                                    <Play className="w-4 h-4 md:w-5 md:h-5 text-primary-600 ml-0.5" />
                                </div>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
            <Link to="/shorts" className="flex items-center justify-center gap-1 text-xs md:text-sm text-primary-600 mt-3 hover:underline">
                {language === 'en' ? 'View all videos' : 'Xem tất cả video'}
                <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
            </Link>
        </div>
    )
}

